import { IAddress } from "common/types";

import DrawerLayout from "client/common/components/stackables/DrawerLayout";
import { useDeleteAddressMutation } from "client/accounts/hooks";
import { useCurrentDrawer } from "client/common/hooks/stackables";
import {
  useSubscriptionsQuery,
  useUpdateBulkSubscriptionsMutation,
} from "client/subscriptions/hooks";
import { NON_CANCELED_NON_IMPORTED_SUBSCRIPTION_STATUSES } from "client/subscriptions/constants";

import AddressDrawerFrom from "../forms/AddressDrawerForm";

const noop = () => undefined;

export type AddressUpdateDrawerProps = {
  address: IAddress;
  onSuccess?: (address: IAddress) => void;
};

const AddressUpdateDrawer = ({ address, onSuccess = noop }: AddressUpdateDrawerProps) => {
  const { close } = useCurrentDrawer();
  const {
    data: subscriptionsToUpdate = [],
    isLoading: isSubscriptionsLoading,
    error: subscriptionsError,
  } = useSubscriptionsQuery({
    visible_status: NON_CANCELED_NON_IMPORTED_SUBSCRIPTION_STATUSES,
  });

  // TODO: this multi-step process should really be one web-request to reduce edge cases on errors
  // most likely we want to surface an endpoint to do this special update or "replace" operation.
  // https://app.shortcut.com/vetcove/story/40167/support-client-address-updates
  const {
    mutate: deleteAddress,
    isLoading: isDeleteLoading,
    isSuccess: isDeleteSuccess,
  } = useDeleteAddressMutation(address.id, {
    onSuccess: close,
  });

  const { mutate: bulkUpdateSubscriptions, isLoading: isBulkUpdateSubscriptionsLoading } =
    useUpdateBulkSubscriptionsMutation({
      onSuccess: () => deleteAddress(),
    });

  const handleSuccess = (newAddress: IAddress) => {
    onSuccess(newAddress);
    const affectedSubscriptionIds = subscriptionsToUpdate
      .filter((subscription) => subscription.shipping_address?.id === address.id)
      .map((subscription) => subscription.id);
    if (affectedSubscriptionIds.length > 0) {
      bulkUpdateSubscriptions({
        shipping_address_id: newAddress.id,
        subscription_ids: affectedSubscriptionIds,
      });
    } else {
      deleteAddress();
    }
  };

  if (subscriptionsError) {
    return (
      <DrawerLayout>
        <DrawerLayout.Header title="Update address" onClose={close} />
        <DrawerLayout.FetchError entityName="autoships" error={subscriptionsError} />
      </DrawerLayout>
    );
  }

  return (
    <DrawerLayout>
      <DrawerLayout.Header title="Update address" onClose={close} />
      <AddressDrawerFrom
        onSuccess={handleSuccess}
        isLoading={
          isSubscriptionsLoading ||
          isDeleteLoading ||
          isDeleteSuccess ||
          isBulkUpdateSubscriptionsLoading
        }
        isPrimary={true}
        initialValues={{
          address_one: address.address_one,
          address_two: address.address_two ?? "",
          city: address.city,
          state: address.state,
          zipcode: address.zipcode,
        }}
        submitText="Update address"
      />
    </DrawerLayout>
  );
};

export default AddressUpdateDrawer;
